"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { usePublicPathname } from "@/lib/public-pathname";

type Variant = "primary" | "secondary" | "tertiary";

type LinkButtonBlok = SbBlokData & {
  label?: string;
  link?: StoryblokLink;
  action?: BlokAction | "";
  variant?: Variant;
};

const variantClasses: Record<Variant, string> = {
  primary: "bg-teal-07 text-white text-[16px] max-[359px]:text-button",
  secondary: "bg-teal-03 text-black text-[16px] max-[359px]:text-button",
  tertiary: "bg-teal-02 text-black text-[16px] max-[359px]:text-button",
};

export default function LinkButton({ blok }: { blok: LinkButtonBlok }) {
  const pathname = usePublicPathname();
  const region = regionFromPathname(pathname);
  const { href, target } = resolveLink(blok.link, region);
  const variant = (blok.variant as Variant) || "primary";
  const triggerAction = useTriggerAction();
  const action = blok.action || null;

  const className = `flex min-h-11 w-full items-center justify-between gap-3 rounded-full px-6 py-2.5 font-semibold leading-tight tracking-[0.02em] transition-opacity hover:opacity-90 max-[359px]:px-5 ${variantClasses[variant]}`;
  const inner = (
    <>
      <span>{blok.label}</span>
      <svg
        width="17"
        height="11"
        viewBox="0 0 17 11"
        fill="currentColor"
        aria-hidden
        className="shrink-0"
      >
        <path d="M15.53 6.49H1c-.55 0-1-.45-1-1s.45-1 1-1h14.53c.55 0 1 .45 1 1s-.45 1-1 1Z" />
        <path d="M15.53 6.49c-.23 0-.45-.08-.64-.23L9.48 1.77c-.42-.35-.48-.98-.13-1.41.35-.43.98-.48 1.41-.13l5.41 4.49c.42.35.48.98.13 1.41-.2.24-.48.36-.77.36Z" />
        <path d="M10.12 10.99c-.29 0-.57-.12-.77-.36-.35-.42-.29-1.06.13-1.41l5.41-4.49c.42-.35 1.06-.3 1.41.13.35.42.29 1.06-.13 1.41l-5.41 4.49c-.19.16-.41.23-.64.23Z" />
      </svg>
    </>
  );

  if (action) {
    return (
      <button
        type="button"
        {...storyblokEditable(blok)}
        onClick={(e) => triggerAction(action, e)}
        className={`cursor-pointer ${className}`}
      >
        {inner}
      </button>
    );
  }

  return (
    <a {...storyblokEditable(blok)} href={href} target={target} className={className}>
      {inner}
    </a>
  );
}
